import React from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Typography, Card, CardActionArea, CardContent, Box } from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import ManualSelectionIcon from "@mui/icons-material/TouchApp";
import CompareIcon from "@mui/icons-material/Compare";
import ImageIcon from "@mui/icons-material/Image";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import { SELECTION_KINDS } from "../../helper/selectionFlow";

const CARD_SX = {
    borderRadius: 3,
    borderColor: "rgba(15, 23, 42, 0.08)",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
        transform: "translateY(-2px)",
    },
};

/** The selection types on offer, each opening its own list. */
const SELECTION_TYPE_CARDS = [
    {
        kind: SELECTION_KINDS.logo,
        icon: <PaletteIcon sx={{ fontSize: 56 }} color="primary" />,
        description: "Filter the exploration samples, compose the palette, verify its spatial repeatability.",
    },
    {
        kind: SELECTION_KINDS.manual,
        icon: <ManualSelectionIcon sx={{ fontSize: 56 }} color="primary" />,
        description: "Pick every palette sample yourself, on the marked plates or by sample ID.",
    },
    {
        kind: SELECTION_KINDS.modeContrast,
        icon: <CompareIcon sx={{ fontSize: 56 }} color="primary" />,
        description: "Palette colors that are close in one capture mode and far apart in the other, close in both modes, or stable across the modes.",
    },
    {
        kind: SELECTION_KINDS.image,
        icon: <ImageIcon sx={{ fontSize: 56 }} color="primary" />,
        description: "Primaries for image and gradient reproduction, picked per congruent-width group.",
    },
];

// Step 1 of the selection workflow: pick the kind of selection. Each type
// opens its own list, where New starts a selection of that type.
export default function ColorSelectionTypeChoice() {
    const navigate = useNavigate();

    // The combined list holds every type; the choice opens the wizard of the chosen one.
    const choose = (kind) => navigate(`${kind.listRoute}/new/name`);

    return (
        <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
            <ExplorationPageShell
                title="Selection"
                subtitle="Select the kind of selection to run."
            >
                <Box
                    sx={{
                        display: "grid",
                        gap: 2.5,
                        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    }}
                >
                    {SELECTION_TYPE_CARDS.map(({ kind, icon, description }) => (
                        <Card key={kind.type} variant="outlined" sx={CARD_SX}>
                            <CardActionArea onClick={() => choose(kind)} sx={{ height: "100%" }}>
                                <CardContent>
                                    <Stack direction="column" alignItems="center" spacing={2} sx={{ py: 3 }}>
                                        {icon}
                                        <Typography variant="h6" align="center">{kind.typeLabel}</Typography>
                                        <Typography variant="body2" color="text.secondary" align="center">
                                            {description}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    ))}
                </Box>
            </ExplorationPageShell>
        </Stack>
    );
}
